import { useLocation } from "react-router-dom";
import { ROUTES_CONFIG } from "./routes-config";
import type { RoutesConfig } from "./types";

/**
 * Finds the most specific route that is shown in the sidebar for a given pathname.
 * This traverses the routes config tree and returns the deepest route that:
 * 1. Matches the current pathname
 * 2. Has showInNav: true
 * 3. Is along the path to the current location
 *
 * @example
 * // For pathname "/messages/inbox/sms/new"
 * // Returns "/messages/inbox" (the most specific nav route)
 *
 * // For pathname "/messages/drafts-sent/drafts"
 * // Returns "/messages/drafts-sent" (the most specific nav route)
 */
function findMostSpecificNavRoute(
  pathname: string,
  config: RoutesConfig = ROUTES_CONFIG,
  basePath = ""
): string | null {
  let mostSpecificNavRoute: string | null = null;

  // Iterate through config entries
  for (const [route, routeConfig] of Object.entries(config)) {
    const fullPath = basePath + route;

    // Check if this route matches the pathname
    if (isRouteMatch(pathname, fullPath)) {
      // If this route is shown in nav, it's a candidate
      if (routeConfig.showInNav) {
        mostSpecificNavRoute = fullPath;
      }

      // Recursively check subRoutes for more specific matches
      if (routeConfig.subRoutes) {
        const subRouteMatch = findMostSpecificNavRoute(pathname, routeConfig.subRoutes, fullPath);

        // If we found a more specific nav route in subRoutes, use it
        if (subRouteMatch) {
          mostSpecificNavRoute = subRouteMatch;
        }
      }
    }
  }

  return mostSpecificNavRoute;
}

/**
 * Checks if a pathname matches a route pattern.
 * Handles dynamic segments like /:id, /:enduserId, etc.
 */
function isRouteMatch(pathname: string, routePattern: string): boolean {
  const pathSegments = pathname.split("/").filter(Boolean);
  const routeSegments = routePattern.split("/").filter(Boolean);

  // Route must have at most as many segments as the pathname
  if (routeSegments.length > pathSegments.length) {
    return false;
  }

  // Check each segment
  for (let i = 0; i < routeSegments.length; i++) {
    const routeSegment = routeSegments[i];
    const pathSegment = pathSegments[i];

    // Dynamic segment (starts with :) matches any value
    if (routeSegment?.startsWith(":")) {
      continue;
    }

    // Static segment must match exactly
    if (routeSegment !== pathSegment) {
      return false;
    }
  }

  return true;
}

/**
 * Hook that determines if a given path is "active" based on the current location.
 * A route is considered active if it is the most specific route displayed in the
 * sidebar according to the routes config.
 *
 * @param path - The path to check for active state
 * @returns true if the path is active, false otherwise
 *
 * @example
 * // Current pathname: /messages/drafts-sent/drafts
 * const isActive = useIsRouteActive("/messages/drafts-sent"); // true
 * const isInboxActive = useIsRouteActive("/messages/inbox"); // false
 *
 * @example
 * // Current pathname: /messages/inbox
 * const isActive = useIsRouteActive("/messages/inbox"); // true
 *
 * @example
 * // Current pathname: /messages/inbox/sms/new
 * const isActive = useIsRouteActive("/messages/inbox"); // true
 * const isDraftActive = useIsRouteActive("/messages/drafts-sent"); // false
 */
export function useIsRouteActive(path: string): boolean {
  const location = useLocation();
  const currentPathname = location.pathname;

  // Find the most specific nav route for the current pathname
  const activeRoute = findMostSpecificNavRoute(currentPathname);

  // The provided path is active if it matches the most specific nav route
  return activeRoute === path;
}
